// Écran « Inspections » : les VGP et contrôles techniques de toute la flotte.
//
// POURQUOI CET ÉCRAN EXISTE
//
// Jusqu'ici une inspection ne se voyait QUE depuis la fiche de son véhicule.
// Pour retrouver un contrôle commencé, il fallait déjà savoir sur quel camion
// il avait été démarré — sur 108 véhicules, cela revient à ne pas pouvoir le
// retrouver. Un brouillon oublié n'est pas un détail d'affichage : la VGP
// n'est pas faite, et personne ne le sait.
//
// LES BROUILLONS EN TÊTE, quelle que soit leur date. Le serveur les trie déjà
// ainsi ; l'écran ne réordonne rien. Une liste triée sur la seule date
// enterrerait sous l'historique ce qui réclame une action aujourd'hui — même
// raisonnement que les échéances à renseigner, qui passent derrière ce qui
// demande d'agir.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import api, { chargerTousLesVehicules } from "../api";
import ComboBox from "../components/ComboBox";
import Layout from "../components/Layout";
import { SqueletteTableau } from "../components/Squelette";
import { formaterDate } from "../format";
import { useModules } from "../modules";

const TYPES = {
  vgp_hayon: "VGP hayon",
  controle_technique: "Contrôle technique",
};

const STATUTS = {
  en_cours: "En cours",
  terminee: "Terminée",
};

export default function Inspections({ user, onLogout }) {
  const navigate = useNavigate();
  const modules = useModules();

  const [lignes, setLignes] = useState(null); // null = pas encore chargé
  const [erreur, setErreur] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");
  const [filtreType, setFiltreType] = useState("");

  // Démarrage d'une inspection depuis CET écran, sans passer par la fiche.
  const [vehicules, setVehicules] = useState([]);
  // LA FLOTTE EST-ELLE ARRIVÉE ? Tant qu'elle ne l'est pas, « Démarrer »
  // cherchait l'immatriculation saisie dans une liste VIDE et répondait
  // « Choisissez un véhicule dans la liste » — à quelqu'un qui venait
  // précisément d'en choisir un. On reprochait à l'utilisateur une faute
  // qu'il n'avait pas commise, et le message le renvoyait vers le seul
  // endroit où il ne trouverait rien.
  //
  // Le défaut existait avant le chargement complet de la flotte (15/09/2026) ;
  // celui-ci l'a simplement rendu plus probable, et un parcours l'a attrapé.
  const [flotteChargee, setFlotteChargee] = useState(false);
  const [choix, setChoix] = useState("");
  const [typeADemarrer, setTypeADemarrer] = useState("controle_technique");
  const [demarrage, setDemarrage] = useState(false);

  const charger = useCallback(() => {
    const params = {};
    if (filtreStatut) params.statut = filtreStatut;
    if (filtreType) params.type_inspection = filtreType;
    api
      .get("/inspections", { params })
      .then(({ data }) => setLignes(data))
      .catch(() => setErreur("Impossible de charger les inspections."));
  }, [filtreStatut, filtreType]);

  useEffect(charger, [charger]);

  useEffect(() => {
    // Le sélecteur doit proposer TOUTE la flotte, sinon le camion cherché est
    // justement celui qui manque. « page_size: 500 » ne le garantissait pas :
    // 500 est le PLAFOND de la route, donc au-delà la liste était tronquée
    // sans un mot. `chargerTousLesVehicules` parcourt les pages.
    chargerTousLesVehicules()
      .then(setVehicules)
      .catch(() => {
        /* Le sélecteur restera vide : la liste, elle, s'affiche quand même. */
      })
      // Dans les DEUX cas : on a fini d'attendre. Ne lever le drapeau qu'en
      // cas de succès laisserait le bouton désactivé pour toujours après une
      // erreur réseau, sans rien dire — on aurait remplacé un faux message
      // par un écran mort.
      .finally(() => setFlotteChargee(true));
  }, []);

  // On ne propose une VGP que sur les véhicules équipés d'un hayon : le
  // serveur refuse les autres (400), et proposer une porte fermée passe pour
  // un bug plutôt que pour une règle.
  const eligibles = useMemo(
    () =>
      typeADemarrer === "vgp_hayon"
        ? vehicules.filter((v) => v.hayon)
        : vehicules,
    [vehicules, typeADemarrer]
  );

  const demarrer = () => {
    const vehicule = eligibles.find((v) => v.immatriculation === choix);
    if (!vehicule) {
      setErreur("Choisissez un véhicule dans la liste.");
      return;
    }
    setDemarrage(true);
    setErreur("");
    api
      .post(`/vehicules/${vehicule.id}/inspections`, {
        type_inspection: typeADemarrer,
      })
      .then(({ data }) => {
        const prefixe = typeADemarrer === "vgp_hayon" ? "vgp" : "ct";
        navigate(`/vehicules/${vehicule.id}/${prefixe}/${data.id}`);
      })
      .catch((err) =>
        setErreur(
          err?.response?.data?.detail || "Impossible de démarrer l'inspection."
        )
      )
      .finally(() => setDemarrage(false));
  };

  // Le module non souscrit ne se devine pas ici : le menu renvoie déjà vers la
  // page de présentation. Ce garde-fou couvre l'adresse tapée à la main.
  if (modules.charge && !modules.module_vgp) {
    return <Navigate to="/modules/inspections" replace />;
  }

  const ouvrir = (l) => {
    const prefixe = l.type_inspection === "vgp_hayon" ? "vgp" : "ct";
    navigate(`/vehicules/${l.vehicule_id}/${prefixe}/${l.id}`);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h2>Inspections</h2>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      <section className="carte">
        <div className="entete-section">
          <h3>Démarrer une inspection</h3>
        </div>
        <div className="grille-form">
          <label className="champ">
            <span className="champ-label">Type</span>
            <select
              value={typeADemarrer}
              onChange={(e) => {
                setTypeADemarrer(e.target.value);
                setChoix("");
              }}
            >
              <option value="controle_technique">Contrôle technique</option>
              <option value="vgp_hayon">VGP hayon</option>
            </select>
          </label>
          <label className="champ">
            <span className="champ-label">Véhicule</span>
            <ComboBox
              value={choix}
              onChange={setChoix}
              options={eligibles.map((v) => v.immatriculation)}
              placeholder="Chercher une immatriculation…"
            />
            {typeADemarrer === "vgp_hayon" && (
              <span className="note-discrete">
                Seuls les véhicules équipés d’un hayon sont proposés.
              </span>
            )}
          </label>
        </div>
        <div className="actions-form">
          <button
            className="btn-accent"
            onClick={demarrer}
            disabled={demarrage || !flotteChargee}
          >
            {demarrage
              ? "Ouverture…"
              : flotteChargee
              ? "Démarrer"
              : "Chargement de la flotte…"}
          </button>
        </div>
      </section>

      <section className="carte">
        <div className="entete-section">
          <h3>Historique</h3>
          <div className="filtres">
            <select
              value={filtreStatut}
              onChange={(e) => setFiltreStatut(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminées</option>
            </select>
            <select
              value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
            >
              <option value="">Tous les types</option>
              <option value="controle_technique">Contrôle technique</option>
              <option value="vgp_hayon">VGP hayon</option>
            </select>
          </div>
        </div>

        {/* `SqueletteTableau` rend un <tbody> : il vit DANS le tableau, jamais
            à côté — un <tbody> orphelin est un HTML invalide que React
            signale, et le navigateur le déplace hors de la carte. L'en-tête
            reste donc affiché pendant le chargement, ce qui dit d'ailleurs au
            client ce qui arrive. */}
        {lignes !== null && lignes.length === 0 ? (
          <p className="vide">
            Aucune inspection. Démarrez un contrôle technique ou une VGP
            ci-dessus, ou depuis la fiche d’un véhicule.
          </p>
        ) : (
          <table className="tableau">
            <thead>
              <tr>
                <th>Véhicule</th>
                <th>Type</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Défauts</th>
              </tr>
            </thead>
            {lignes === null ? (
              <SqueletteTableau lignes={6} colonnes={5} />
            ) : (
            <tbody>
              {lignes.map((l) => (
                <tr
                  key={l.id}
                  className={
                    "cliquable" +
                    (l.statut === "en_cours" ? " urgence-rappel" : "")
                  }
                  onClick={() => ouvrir(l)}
                >
                  <td>
                    <strong>{l.immatriculation || "—"}</strong>
                    {l.marque_vehicule && (
                      <span className="note-discrete"> · {l.marque_vehicule}</span>
                    )}
                  </td>
                  <td>{TYPES[l.type_inspection] || l.type_inspection}</td>
                  <td>{formaterDate(l.date_controle)}</td>
                  <td>{STATUTS[l.statut] || l.statut}</td>
                  <td>{l.nb_defauts || "—"}</td>
                </tr>
              ))}
            </tbody>
            )}
          </table>
        )}
      </section>
    </Layout>
  );
}
